import axios from 'axios';
import request from 'store/utils/request';
import {
  partialExportAction,
  cancelPartialExportAction,
  partialImportAndMergeAction,
  partialImportAndReplaceAction,
} from 'store/shared/actions';
import ErrorHandler from 'store/utils/ErrorHandler';
import Notify from 'store/utils/Notify';
import { getCurrentPage } from 'store/utils/pagination';

import {
  getRuleGroupsList,
  deselectRuleGroup,
} from 'store/services/ruleGroups/actions';
import {
  getRuleGroupsList as ruleGroupsList,
  getPaginationOptions,
} from 'store/services/ruleGroups/selectors';

import * as ruleEntitiesActions from 'store/services/ruleEntities/actions';

import * as contextsApi from '../contexts/api';
import * as ruleGroupsApi from '../ruleGroups/api';

import * as api from './api';
import * as types from './actionTypes';
import * as selectors from './selectors';

import { getIDsOfRelatedEntities } from '../../utils/ruleEntities';

const getRuleRequestId = 'get-rule';

/**
 * Add a rule
 *
 * @param groupId
 * @param data
 */
export const addRule = (groupId, data) => async (dispatch, getState) => {
  try {
    await dispatch({
      type: types.ADD_RULE_REQUEST,
    });

    const response = await api.addRuleRequest(groupId, data);

    await dispatch({
      type: types.ADD_RULE_SUCCESS,
      payload: {
        item: response.data,
      },
    });

    await dispatch(deselectRuleGroup());

    await dispatch(
      getRuleGroupsList({
        page: getCurrentPage(
          getPaginationOptions(getState()).page,
          ruleGroupsList(getState()),
        ),
      }),
    );
    Notify.success({
      title: 'Rule was created successfully',
    });

    return Promise.resolve({
      id: response.data.id,
      groupId: response.data.rule_group,
    });
  } catch (error) {
    await dispatch({
      type: types.ADD_RULE_ERROR,
      payload: {
        error: error.message,
      },
    });

    Notify.error({
      title: 'Fail to add RUle',
      description: ErrorHandler.getNotificationError(error),
    });

    return error;
  }
};

/**
 * Get rule
 *
 * @param id
 * @param groupId
 */
export const getRule = (id, groupId) => async (dispatch, getState) => {
  try {
    dispatch({
      type: types.GET_RULE_REQUEST,
    });

    request.cancel(getRuleRequestId);
    const { data } = await api.getRuleRequest(groupId, id, getRuleRequestId);

    if (data) {
      const ids = getIDsOfRelatedEntities(data);

      if (ids.length > 0) {
        const rulePayload = {
          stock: true,
          id__in: ids.join(','),
        };

        ruleEntitiesActions.getRuleEntitiesList(rulePayload)(
          dispatch,
          getState,
        );
      }

      const groupDetails = data.rule_group
        ? (await ruleGroupsApi.getRuleGroupsRequest(data.rule_group)).data
        : null;

      const contextDetails = data.context
        ? (await contextsApi.getContextRequest(data.context)).data
        : null;

      dispatch({
        type: types.GET_RULE_SUCCESS,
        payload: {
          item: {
            ...data,
            groupDetails,
            contextDetails,
          },
        },
      });
    }
  } catch (error) {
    if (!axios.isCancel(error)) {
      await dispatch({
        type: types.GET_RULE_ERROR,
        payload: {
          error: error.message,
        },
      });

      Notify.error({
        title: 'An error occurred',
        description: ErrorHandler.getNotificationError(error),
      });
    }
  }
};

/**
 * Reorder rule
 *
 * @param id
 * @param data
 * @param groupId
 */
export const reorderRule = (id, data, groupId) => async (
  dispatch,
  getState,
) => {
  try {
    await dispatch({
      type: types.REORDER_RULE_REQUEST,
    });
    const state = getState();

    await api.updateRuleRequest(groupId, id, data);

    await dispatch({
      type: types.REORDER_RULE_SUCCESS,
    });

    await dispatch(getRuleGroupsList());

    if (selectors.getRule(state).id === id) {
      await dispatch(getRule(id, groupId));
    }

    Notify.success({
      title: 'Rule was reordered successfully',
    });
  } catch (error) {
    await dispatch({
      type: types.REORDER_RULE_ERROR,
      payload: {
        error: error.message,
      },
    });

    Notify.error({
      title: 'Fail to reorder Rule',
      description: ErrorHandler.getNotificationError(error),
    });
  }
};

/**
 * Partial update rule
 *
 * @param id
 * @param groupId
 * @param data
 * @param action
 */
export const partialUpdateRule = (id, data, groupId, action = '') => async (
  dispatch,
  getState,
) => {
  try {
    await dispatch({
      type: types.PARTIAL_UPDATE_RULE_REQUEST,
    });

    const response = await api.partialUpdateRuleRequest(groupId, id, data);

    await dispatch({
      type: types.PARTIAL_UPDATE_RULE_SUCCESS,
    });

    await dispatch(getRuleGroupsList());

    const groupDetails = response.data.rule_group
      ? (await ruleGroupsApi.getRuleGroupsRequest(response.data.rule_group))
          .data
      : null;

    // Update current rule if it is open
    if (selectors.getRule(getState()).id === id) {
      await dispatch({
        type: types.UPDATE_RULE_SUCCESS,
        payload: {
          item: {
            ...response.data,
            groupDetails,
          },
        },
      });
    }

    if (action === 'move') {
      Notify.success({
        title: 'Rule was moved successfully',
      });
    }

    return Promise.resolve({
      id: response.data.id,
      groupId: response.data.rule_group,
    });
  } catch (error) {
    await dispatch({
      type: types.PARTIAL_UPDATE_RULE_ERROR,
      payload: {
        error: error.message,
      },
    });

    Notify.error({
      title: 'Fail to move Rule',
      description: ErrorHandler.getNotificationError(error),
    });

    return error;
  }
};

/**
 * Update rule
 *
 * @param id
 * @param payload
 * @param action
 * @param groupId
 * @param newGroupId
 */
export const updateRule = (id, payload, action = '', groupId) => async (
  dispatch,
  getState,
) => {
  try {
    dispatch({
      type: types.UPDATE_RULE_REQUEST,
    });

    const { data } = await api.updateRuleRequest(groupId, id, payload);
    if (data) {
      const ids = getIDsOfRelatedEntities(data);

      if (ids.length > 0) {
        const rulePayload = {
          stock: true,
          id__in: ids.join(','),
        };

        ruleEntitiesActions.getRuleEntitiesList(rulePayload)(
          dispatch,
          getState,
        );
      }
    }

    const groupDetails = data.rule_group
      ? (await ruleGroupsApi.getRuleGroupsRequest(data.rule_group)).data
      : null;

    const contextDetails = data.context
      ? (await contextsApi.getContextRequest(data.context)).data
      : null;

    dispatch({
      type: types.UPDATE_RULE_SUCCESS,
      payload: {
        item: {
          ...data,
          groupDetails,
          contextDetails,
        },
      },
    });

    dispatch(getRuleGroupsList({}, {}, true));

    if (action === 'enabled' && !data.enabled) {
      Notify.warning({
        title: 'Warning!',
        description: `Context rule "${data.name}" has been disabled`,
      });
    } else {
      Notify.success({
        title: 'Rule was updated successfully',
      });
    }

    return Promise.resolve({
      id: data.id,
      groupId: data.rule_group,
    });
  } catch (error) {
    dispatch({
      type: types.UPDATE_RULE_ERROR,
      payload: {
        error: error.message,
      },
    });

    Notify.error({
      title: 'Fail to update Rule',
      description: ErrorHandler.getNotificationError(error),
    });

    return error;
  }
};

/**
 * Select a rule
 *
 * @param item
 */

export const selectRule = item => dispatch => {
  dispatch(deselectRuleGroup());
  dispatch({
    type: types.SELECT_RULE,
    payload: {
      item,
    },
  });
};

export const partialExportAllRules = partialExportAction({
  path: 'rule_groups',
  filename: 'rules',
  types: {
    complete: types.PARTIAL_EXPORT_RULE_COMPLETE,
    request: types.PARTIAL_EXPORT_RULE_REQUEST,
  },
});

export const partialExportGroupRule = partialExportAction({
  path: 'rule_groups',
  filename: 'rule-group',
  types: {
    complete: types.PARTIAL_EXPORT_RULE_COMPLETE,
    request: types.PARTIAL_EXPORT_RULE_REQUEST,
  },
});

export const partialExportSingleRule = partialExportAction({
  path: 'rule_groups',
  filename: 'rule',
  queryKey: 'rule__id__in',
  types: {
    complete: types.PARTIAL_EXPORT_RULE_COMPLETE,
    request: types.PARTIAL_EXPORT_RULE_REQUEST,
  },
});

export const cancelExportRules = cancelPartialExportAction({
  path: 'rule_groups',
  types: {
    cancel: types.PARTIAL_EXPORT_RULE_CANCEL,
  },
});

export const partialImportAndMergeRules = partialImportAndMergeAction({
  path: 'rule_groups',
  postHook: dispatch => dispatch(getRuleGroupsList({ page: 1 }, {}, true)),
  types: {
    complete: types.PARTIAL_IMPORT_RULE_COMPLETE,
    request: types.PARTIAL_IMPORT_RULE_REQUEST,
  },
});

export const partialImportAndReplaceRules = partialImportAndReplaceAction({
  path: 'rule_groups',
  postHook: dispatch => dispatch(getRuleGroupsList({ page: 1 }, {}, true)),
  types: {
    complete: types.PARTIAL_IMPORT_RULE_COMPLETE,
    request: types.PARTIAL_IMPORT_RULE_REQUEST,
  },
});
/**
 * Deselect a rule
 */
export const deselectRule = () => ({
  type: types.DESELECT_RULE,
});

/**
 * Delete a rule
 *
 * @param id,
 * @param groupId
 */
export const deleteRule = (id, groupId) => async dispatch => {
  try {
    await dispatch({
      type: types.DELETE_RULE_REQUEST,
    });

    await api.deleteRuleRequest(groupId, id);

    await dispatch({
      type: types.DELETE_RULE_SUCCESS,
    });

    await dispatch(getRuleGroupsList({ page: 1 }, {}, true));

    Notify.success({
      title: 'Rule was deleted successfully',
    });
  } catch (error) {
    await dispatch({
      type: types.DELETE_RULE_ERROR,
      payload: {
        error: error.message,
      },
    });

    Notify.error({
      title: 'Fail to delete Rule',
      description: ErrorHandler.getNotificationError(error),
    });
  }
};
